import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FreightAppDrawerHtmlField from "../../../components/admin/FreightAppDrawerHtmlField";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { getFreightAppDrawerIndex, storeFreightAppDrawer } from "../../../utils/api";
import { normalizeStoredRichHtml } from "../../../utils/freightRichHtml";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const FreightAppDrawerPage = () => {
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "App drawer" }
    ],
    [roleLabel]
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [about, setAbout] = useState("");
  const [website, setWebsite] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [termsOfService, setTermsOfService] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightAppDrawerIndex();
      const d = res?.data ?? {};
      setAbout(normalizeStoredRichHtml(d.about?.about));
      setWebsite(String(d.website?.website ?? "").trim());
      setPrivacyPolicy(normalizeStoredRichHtml(d.privacyPolicy?.privacy_policy));
      setTermsOfService(
        normalizeStoredRichHtml(d.termOfService?.terms_of_service ?? d.termsOfService?.terms_of_service)
      );
    } catch (e) {
      showError(e.message || "Failed to load app drawer content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await storeFreightAppDrawer({
        about,
        website: website.trim(),
        privacy_policy: privacyPolicy,
        terms_of_service: termsOfService
      });
      showSuccess("App drawer content saved");
      await load();
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="freight-admin-shell freight-admin-shell--narrow">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="m-0">Freight app drawer</h3>
            <div className="flex gap-2">
              <button type="button" className="btn-outline text-sm" onClick={() => load()} disabled={loading}>
                Refresh
              </button>
              <Link to="/freight" className="btn-outline no-underline text-sm inline-flex items-center">
                Freight hub
              </Link>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Website</strong> is a plain URL. <strong>About</strong>, <strong>Privacy</strong>, and <strong>Terms</strong> are rich HTML
            (Quill): use <strong>Edit</strong> to change content and <strong>Preview</strong> to see how it will render. Saved HTML is sent to the
            API as before.
          </p>

          {loading ? (
            <p className="text-gray-600">Loading…</p>
          ) : (
            <form className="freight-admin-form space-y-8" onSubmit={onSubmit}>
              <FreightAppDrawerHtmlField
                label="About"
                value={about}
                onChange={setAbout}
                helpText="If the backend returned a full Quill document, the editor strips the outer wrapper and saves only the inner HTML."
              />

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Website</label>
                <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
                <p className="mt-1 m-0 text-xs text-gray-500">Plain text URL only (not HTML).</p>
              </div>

              <FreightAppDrawerHtmlField label="Privacy policy" value={privacyPolicy} onChange={setPrivacyPolicy} />

              <FreightAppDrawerHtmlField label="Terms of service" value={termsOfService} onChange={setTermsOfService} />

              <div className="flex flex-wrap gap-2 pt-2">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightAppDrawerPage;
